import { useCallback, useMemo } from 'react';

import useToast, { ToastTypeVariants } from '@/hooks/useToast';

import { getMessageTextContent } from './getMessageTextContent';
import { type MessageAction, type MessageWithTextContent } from './types';

type UseCopyMessageTextResult = {
  /** The plain text content extracted from the message. */
  textContent: string;
  /** Copies the message text to the clipboard with toast feedback. */
  handleCopyText: () => Promise<void>;
  /** A pre-built action object for use in message action menus, or null if no text content. */
  copyAction: MessageAction | null;
};

/**
 * Hook for copying message text content to clipboard with toast feedback.
 * Provides the extracted text content, a copy handler, and a ready-to-use action object.
 */
export function useCopyMessageText(message: MessageWithTextContent): UseCopyMessageTextResult {
  const addToast = useToast();

  const textContent = useMemo(() => getMessageTextContent(message), [message]);

  const handleCopyText = useCallback(async (): Promise<void> => {
    if (!textContent) return;
    try {
      await navigator.clipboard.writeText(textContent);
      addToast(ToastTypeVariants.SUCCESS, 'Copied to clipboard');
    } catch {
      addToast(ToastTypeVariants.ERROR, 'Failed to copy to clipboard');
    }
  }, [textContent, addToast]);

  const copyAction = useMemo((): MessageAction | null => {
    if (!textContent) return null;
    return {
      icon: 'ph--copy--regular',
      label: 'Copy text',
      onClick: handleCopyText,
    };
  }, [textContent, handleCopyText]);

  return {
    copyAction,
    handleCopyText,
    textContent,
  };
}
